import React, { useMemo, useState } from 'react';
import { getReporteRotativos } from '../services/rotativos.service';
import { getErrorMessage } from '../utils/errorHandler';

const MAX_RANGE_DAYS = 62;

type RotativoRow = {
  userid: string;
  name: string;
  idcard: string;
  fecha: string;
  turno: string;
  tipoDia?: 'LABORAL' | 'DESCANSO';
};

type RotativoCell = {
  turno: string;
  letra: string;
  tipoDia: 'LABORAL' | 'DESCANSO';
};

type RotativoAgent = {
  userid: string;
  name: string;
  idcard: string;
};

const toIsoDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getTodayIso = (): string => toIsoDate(new Date());

const getCurrentMonthStartIso = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
};

const parseIsoDate = (value: string): Date | null => {
  const [y, m, d] = String(value).split('-').map(Number);
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return null;
  return new Date(y, m - 1, d);
};

const toIsoFromDisplay = (value: string): string => {
  const [day, month, year] = String(value).split('/');
  if (!day || !month || !year) return '';
  return `${year}-${month}-${day}`;
};

const buildIsoDates = (desde: string, hasta: string): string[] => {
  const start = parseIsoDate(desde);
  const end = parseIsoDate(hasta);
  if (!start || !end || start > end) return [];

  const result: string[] = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    result.push(toIsoDate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return result;
};

const getWeekdayShort = (isoDate: string): string => {
  const date = parseIsoDate(isoDate);
  if (!date) return '';
  const labels = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
  return labels[date.getDay()] ?? '';
};

const getDayNumber = (isoDate: string): string => {
  const date = parseIsoDate(isoDate);
  if (!date) return '';
  return String(date.getDate());
};

const getTurnoKey = (turno: string): string => turno.trim().toLowerCase();

const getTurnoColorClass = (turno: string): string => {
  const key = getTurnoKey(turno);
  if (!key) return 'rotativos-turno-color-0';

  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash << 5) - hash + key.charCodeAt(i);
    hash |= 0;
  }

  const index = Math.abs(hash) % 8;
  return `rotativos-turno-color-${index}`;
};

const RotativosPage: React.FC = () => {
  const [desde, setDesde] = useState<string>(() => getCurrentMonthStartIso());
  const [hasta, setHasta] = useState<string>(() => getTodayIso());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agents, setAgents] = useState<RotativoAgent[]>([]);
  const [isoDates, setIsoDates] = useState<string[]>([]);
  const [matrix, setMatrix] = useState<Map<string, Map<string, RotativoCell>>>(new Map());

  const turnosLegend = useMemo(() => {
    const unique = new Set<string>();
    for (const dayMap of Array.from(matrix.values())) {
      for (const cell of Array.from(dayMap.values())) {
        if (cell.tipoDia === 'LABORAL' && cell.turno) {
          unique.add(cell.turno);
        }
      }
    }

    return Array.from(unique)
      .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }))
      .map((turno) => ({
        turno,
        letra: turno.trim().charAt(0).toUpperCase(),
        colorClass: getTurnoColorClass(turno),
      }));
  }, [matrix]);

  const validateRange = (): string | null => {
    const start = parseIsoDate(desde);
    const end = parseIsoDate(hasta);

    if (!start || !end) return 'Debe seleccionar fechas validas.';
    if (start > end) return 'La fecha Desde no puede ser mayor que Hasta.';

    const days = buildIsoDates(desde, hasta).length;
    if (days > MAX_RANGE_DAYS) {
      return `El rango maximo permitido es de ${MAX_RANGE_DAYS} dias.`;
    }

    return null;
  };

  const generar = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const validationError = validateRange();
    if (validationError) {
      setAgents([]);
      setIsoDates([]);
      setMatrix(new Map());
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      const response = await getReporteRotativos({
        desde,
        hasta,
        estado: 'TODOS',
      });

      const rows = Array.isArray(response.data?.rows) ? (response.data.rows as RotativoRow[]) : [];
      const dates = buildIsoDates(desde, hasta);

      const byAgent = new Map<string, RotativoAgent>();
      const nextMatrix = new Map<string, Map<string, RotativoCell>>();

      for (const row of rows) {
        const agentId = String(row.userid ?? '').trim();
        if (!agentId) continue;

        if (!byAgent.has(agentId)) {
          byAgent.set(agentId, {
            userid: agentId,
            name: String(row.name ?? ''),
            idcard: String(row.idcard ?? ''),
          });
        }

        const isoDate = toIsoFromDisplay(String(row.fecha ?? ''));
        if (!isoDate) continue;

        const dayMap = nextMatrix.get(agentId) ?? new Map<string, RotativoCell>();
        const tipoDia = row.tipoDia === 'DESCANSO' ? 'DESCANSO' : 'LABORAL';
        const turno = String(row.turno ?? '');

        dayMap.set(isoDate, {
          tipoDia,
          turno,
          letra: tipoDia === 'LABORAL' && turno ? turno.trim().charAt(0).toUpperCase() : '',
        });

        nextMatrix.set(agentId, dayMap);
      }

      const sortedAgents = Array.from(byAgent.values()).sort((a, b) =>
        a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }),
      );

      setAgents(sortedAgents);
      setIsoDates(dates);
      setMatrix(nextMatrix);
    } catch (err) {
      setAgents([]);
      setIsoDates([]);
      setMatrix(new Map());
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <h1 className="page-title">Rotativos</h1>

      <div className="card">
        <h2 className="card-title">Calendario de turnos rotativos</h2>
        {error && <div className="error-msg">{error}</div>}

        <form onSubmit={generar}>
          <div className="filters-grid" style={{ marginBottom: '12px' }}>
            <div className="form-group">
              <label>Desde</label>
              <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Hasta</label>
              <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} required />
            </div>
          </div>

          <div className="filter-actions">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Generando...' : 'Generar calendario'}
            </button>
          </div>
        </form>
      </div>

      {isoDates.length > 0 && (
        <div className="card">
          <div className="rotativos-calendar-header">
            <div>
              <strong>Periodo:</strong> {desde} a {hasta}
            </div>
            <div>
              <strong>Colaboradores:</strong> {agents.length}
            </div>
          </div>

          {turnosLegend.length > 0 && (
            <div className="rotativos-legend" style={{ marginBottom: '12px' }}>
              {turnosLegend.map((item) => (
                <div key={item.turno} className="rotativos-legend-item">
                  <span className={`rotativos-legend-dot ${item.colorClass}`}>{item.letra}</span>
                  <span>{item.turno}</span>
                </div>
              ))}
            </div>
          )}

          {agents.length === 0 ? (
            <div className="empty-state">No hay colaboradores con rotativos en el rango seleccionado.</div>
          ) : (
            <div className="table-wrapper rotativos-calendar-wrapper">
              <table className="rotativos-calendar-table">
                <thead>
                  <tr>
                    <th className="rotativos-sticky-col">Colaborador</th>
                    {isoDates.map((isoDate) => (
                      <th key={isoDate} className="rotativos-day-col">
                        <div className="rotativos-day-head-top">{getWeekdayShort(isoDate)}</div>
                        <div className="rotativos-day-head-bottom">{getDayNumber(isoDate)}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {agents.map((agent) => {
                    const dayMap = matrix.get(agent.userid) ?? new Map<string, RotativoCell>();
                    return (
                      <tr key={agent.userid}>
                        <td className="rotativos-sticky-col">
                          <div className="rotativos-agent-name">{agent.name}</div>
                          <div className="rotativos-agent-sub">{agent.userid}</div>
                        </td>
                        {isoDates.map((isoDate) => {
                          const cell = dayMap.get(isoDate);
                          if (!cell || cell.tipoDia === 'DESCANSO') {
                            return <td key={isoDate} className="rotativos-cell rotativos-cell-rest" />;
                          }

                          return (
                            <td key={isoDate} className={`rotativos-cell rotativos-cell-work ${getTurnoColorClass(cell.turno)}`} title={cell.turno}>
                              {cell.letra}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default RotativosPage;